/* eslint-disable react-refresh/only-export-components -- TanStack Router: Route + co-located page sections */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RotateCcw, StarOff } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { AddComponentDialog } from "@/presentation/components/architecture/add-component-dialog";
import { ArchitectureMapCanvas } from "@/presentation/components/architecture/architecture-map-canvas";
import { ArchitectureNodeFeaturesDialog } from "@/presentation/components/architecture/architecture-node-features-dialog";
import { B2bUsersToolbar } from "@/presentation/components/architecture/b2b-users-toolbar";
import { StageToggle } from "@/presentation/components/architecture/stage-toggle";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { PageHeader } from "@/presentation/components/shell/page-header";
import {
  STAGE_DESCRIPTION,
  STAGE_HEADLINE,
  STAGE_LABEL,
  STAGES,
  type Stage,
} from "@/presentation/data/architecture-components";
import { type MapDomain } from "@/presentation/data/architecture-map-data";
import { type B2bUsersNodeKind } from "@/presentation/data/b2b-users-stage-seed";
import { useArchitectureMapPage } from "@/presentation/hooks/use-architecture-map-page";
import { useB2bUsersMapPage } from "@/presentation/hooks/use-b2b-users-map-page";

const mapSearchSchema = z.object({
  stage: z.enum(["b2c", "b2b", "mvp", "b2b-users"]).catch("b2c").default("b2c"),
});

export const Route = createFileRoute("/_app/architecture/map")({
  validateSearch: mapSearchSchema,
  component: ArchitectureMapPage,
});

function HeroIntro({
  stageLabel,
  stageHeadline,
  stageDescription,
  activeCount,
  totalCount,
  yandexActiveCount,
  editable,
}: {
  stageLabel: string;
  stageHeadline: string;
  stageDescription: string;
  activeCount: number;
  totalCount: number;
  yandexActiveCount: number;
  editable?: boolean;
}) {
  return (
    <div className="hero-panel relative overflow-hidden rounded-2xl">
      <div className="scanlines pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative flex flex-col gap-4 px-7 py-6">
        <span className="eyebrow-bright font-bold">
          Stage · {stageLabel}
          {editable ? (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[oklch(0.55_0.16_55)] px-2 py-0.5 text-[10px] font-bold tracking-wide text-white uppercase">
              <span className="inline-block size-1.5 animate-pulse rounded-full bg-white" />
              Live edit
            </span>
          ) : null}
        </span>
        <h2 className="max-w-3xl text-xl leading-snug font-bold tracking-tight">{stageHeadline}</h2>
        <p className="max-w-3xl text-[13px] leading-relaxed font-medium text-white/88">
          {stageDescription}
        </p>
        {editable ? (
          <p className="max-w-3xl text-[11px] leading-snug font-medium text-[oklch(0.88_0.02_200_/_0.75)]">
            <strong className="text-[oklch(0.85_0.13_55)]">Drag</strong> nodes to reposition ·{" "}
            <strong className="text-[oklch(0.85_0.13_55)]">Drag from a handle</strong> to draw an
            arrow · <strong className="text-[oklch(0.85_0.13_55)]">Double-click a label</strong> to
            rename · Select then <strong className="text-[oklch(0.85_0.13_55)]">Backspace</strong>{" "}
            to delete · Use the toolbar to add components or actors.
          </p>
        ) : (
          <p className="max-w-3xl text-[11px] leading-snug font-medium text-[oklch(0.88_0.02_200_/_0.75)]">
            <strong className="text-[oklch(0.82_0.12_190)]">Tip:</strong> the legend floats at the
            top of the canvas — domains, ownership, and edge colors are always visible. Use pan/zoom
            and <strong className="text-white/90">Reset layout</strong> if saved positions feel
            tight after updates.
          </p>
        )}
        <div className="font-data flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-semibold tracking-wide text-[oklch(0.85_0.01_240_/_0.65)]">
          <span>
            <span className="text-[oklch(0.72_0.17_190)]">{activeCount}</span> / {totalCount} nodes
            highlighted
          </span>
          {yandexActiveCount > 0 ? (
            <span>
              <span className="text-[oklch(0.72_0.17_190)]">{yandexActiveCount}</span> Yandex-stack
              products in this stage
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ArchitectureMapPage() {
  const { stage } = Route.useSearch();

  return (
    <div className="flex flex-col">
      <div className="flex justify-end px-6 pt-6 print:hidden">
        <PageExplainer pageKey="architecture" />
      </div>
      <PageHeader
        tone="dark"
        eyebrow="Architecture · stakeholder map"
        title="End-to-end map — people, money, and feedback loops"
        summary={
          <span>
            <strong className="text-white/95">Bold</strong> copy and color-coded edges are
            intentional: this page is optimized for stakeholder read-through across the full stage
            model.
          </span>
        }
      />
      {stage === "b2b-users" ? <B2bUsersStageBody /> : <SharedStageBody stage={stage} />}
    </div>
  );
}

/**
 * Editable shared workspace for b2c / b2b / mvp stages.
 * One workspace (map_nodes + map_edges) backs all three stages; only
 * map_stage_highlights[stage] differs between them.
 */
function SharedStageBody({ stage }: { stage: Stage }) {
  const page = useArchitectureMapPage(stage);
  const [addDialog, setAddDialog] = useState<{ open: boolean; kind: B2bUsersNodeKind }>({
    open: false,
    kind: "system",
  });

  return (
    <div className="animate-page-enter flex flex-col gap-5 px-6 py-6">
      <HeroIntro
        stageLabel={page.stageLabel}
        stageHeadline={page.stageHeadline}
        stageDescription={page.stageDescription}
        activeCount={page.activeCount}
        totalCount={page.totalCount}
        yandexActiveCount={page.yandexActiveCount}
        editable
      />
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <StageToggle value={page.stage} onChange={page.selectStage} stages={page.stages} />
        <div className="flex flex-wrap items-center gap-2">
          {page.hasStageOverride ? (
            <button
              type="button"
              onClick={page.resetStageHighlights}
              className="font-data text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 rounded-md border border-[oklch(0.91_0.008_240)] bg-white px-3 py-1.5 text-[11px] font-semibold tracking-tight transition-colors hover:bg-[oklch(0.95_0.01_240)]"
              title="Restore the default highlights for this stage"
            >
              <StarOff className="size-3.5" />
              Reset {page.stageLabel.toLowerCase()} highlights
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  "Reset the shared map (b2c/b2b/mvp) to its seed graph? This clears all components, edges, positions, and per-stage highlights."
                )
              ) {
                page.resetLayout();
              }
            }}
            className="font-data text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 rounded-md border border-[oklch(0.91_0.008_240)] bg-white px-3 py-1.5 text-[11px] font-semibold tracking-tight transition-colors hover:bg-[oklch(0.95_0.01_240)]"
            title="Reset shared map to seed"
          >
            <RotateCcw className="size-3.5" />
            Reset map
          </button>
        </div>
      </div>
      <ArchitectureMapCanvas
        nodes={page.nodes}
        edges={page.edges}
        onNodesChange={page.onNodesChange}
        onEdgesChange={page.onEdgesChange}
        onConnect={page.onConnect}
        onNodeClick={page.onNodeClick}
        onPaneClick={page.onPaneClick}
        editable
        topRightPanel={
          <B2bUsersToolbar
            nodeCount={page.totalCount}
            edgeCount={page.edges.length}
            onAddSystem={() => setAddDialog({ open: true, kind: "system" })}
            onAddActor={() => setAddDialog({ open: true, kind: "actor" })}
            onReset={() => {
              if (
                window.confirm(
                  "Reset the shared map (b2c/b2b/mvp) to its seed graph? This clears all components, edges, positions, and per-stage highlights."
                )
              ) {
                page.resetLayout();
              }
            }}
          />
        }
      />
      <ArchitectureNodeFeaturesDialog nodeId={page.dialogNodeId} onClose={page.closeDialog} />
      <AddComponentDialog
        open={addDialog.open}
        defaultKind={addDialog.kind}
        defaultDomain={addDialog.kind === "actor" ? "customer-experience" : "app-core"}
        onClose={() => setAddDialog((s) => ({ ...s, open: false }))}
        onSubmit={(values) => {
          // Translate AddComponentFormValues (label/Domain) into the hook's
          // addNode shape (name/MapDomain). Actors live in the "actors" map
          // domain regardless of the form's domain choice.
          const mapDomain: MapDomain = values.kind === "actor" ? "actors" : values.domain;
          page.addNode({
            name: values.label,
            role: values.role,
            domain: mapDomain,
            kind: values.kind,
            ownership: "yandex",
          });
          setAddDialog((s) => ({ ...s, open: false }));
        }}
      />
    </div>
  );
}

function B2bUsersStageBody() {
  const navigate = useNavigate({ from: "/architecture/map" });
  const page = useB2bUsersMapPage();
  const [addDialog, setAddDialog] = useState<{ open: boolean; kind: B2bUsersNodeKind }>({
    open: false,
    kind: "system",
  });

  function selectStage(next: Stage) {
    void navigate({ search: { stage: next } });
  }

  return (
    <div className="animate-page-enter flex flex-col gap-5 px-6 py-6">
      <HeroIntro
        stageLabel={STAGE_LABEL["b2b-users"]}
        stageHeadline={STAGE_HEADLINE["b2b-users"]}
        stageDescription={STAGE_DESCRIPTION["b2b-users"]}
        activeCount={page.nodeCount}
        totalCount={page.nodeCount}
        yandexActiveCount={0}
        editable
      />
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <StageToggle value="b2b-users" onChange={selectStage} stages={STAGES} />
      </div>
      <ArchitectureMapCanvas
        nodes={page.nodes}
        edges={page.edges}
        onNodesChange={page.onNodesChange}
        onEdgesChange={page.onEdgesChange}
        onConnect={page.onConnect}
        onNodeClick={() => {
          /* no dialog in editable stage — clicks select for delete */
        }}
        onPaneClick={() => {}}
        editable
        topRightPanel={
          <B2bUsersToolbar
            nodeCount={page.nodeCount}
            edgeCount={page.edgeCount}
            onAddSystem={() => setAddDialog({ open: true, kind: "system" })}
            onAddActor={() => setAddDialog({ open: true, kind: "actor" })}
            onReset={() => {
              if (window.confirm("Reset the B2B users stage to the seed graph?")) {
                page.resetToSeed();
              }
            }}
          />
        }
      />
      <AddComponentDialog
        open={addDialog.open}
        defaultKind={addDialog.kind}
        defaultDomain={addDialog.kind === "actor" ? "customer-experience" : "app-core"}
        onClose={() => setAddDialog((s) => ({ ...s, open: false }))}
        onSubmit={(values) => {
          page.addNode(values);
          setAddDialog((s) => ({ ...s, open: false }));
        }}
      />
    </div>
  );
}
